/**
 * @fileoverview Board Skill Handler
 *
 * ハンドラ契約:
 * - scatterPieces(): シャッフルアニメーション（オプション）
 * - execute(def, target): 実行（必須）
 */

import { ROWS, COLS, PIECE_SIZE } from '../../../core/constants.js';
import { syncBoardPieces } from '../../../shared/animations-public.js';
import { MinimalErrorTracker } from '../../../shared/log.js';
import { runCascadeFromRemovals } from '../../cascade.js';
import { getGameBoard } from '../../game-controls.js';

/**
 * 放物線アニメーション（ゼロベース版）
 * 移動方向に垂直な方向へベジェ曲線を描く
 */
function animateArc({ cell, translateX, translateY, rotation = 0, index = 0, duration = 600 }) {
  const waveDelay = index * 5;

  const dx = translateX;
  const dy = translateY;
  const dist = Math.hypot(dx, dy);

  // ほぼ静止は線形で終了
  if (dist < 0.5) {
    return new Promise((resolve) => {
      setTimeout(() => {
        const anim = cell.animate(
          [
            { transform: 'translate3d(0,0,0) rotate(0deg)', offset: 0 },
            { transform: `translate3d(${dx}px, ${dy}px, 0) rotate(${rotation}deg)`, offset: 1 },
          ],
          { duration, easing: 'cubic-bezier(0.4,0,0.2,1)', fill: 'forwards' },
        );
        anim.addEventListener('finish', resolve);
      }, waveDelay);
    });
  }

  // 直線に垂直な単位ベクトル（右手系）
  const nx = -dy / dist;
  const ny = dx / dist;

  // ふくらみ量：距離比例＋上限。必要なら最小も設定可
  const bendAmp = Math.min(200, dist * 0.3); // 調整ノブ①

  // 制御点（中点＋垂直方向にオフセット）
  const cx = dx * 0.5 + nx * bendAmp;
  const cy = dy * 0.5 + ny * bendAmp;

  // 二次ベジェ補間関数
  const quad = (t, p0, p1, p2) => (1 - t) * (1 - t) * p0 + 2 * (1 - t) * t * p1 + t * t * p2;

  // サンプリング数（多いほど滑らか）
  const SAMPLES = 17; // 調整ノブ②（9〜21推奨）
  const keyframes = [];
  for (let i = 0; i < SAMPLES; i++) {
    // イーズを掛けた進行（滑りを抑える）
    const u = i / (SAMPLES - 1);
    // easeInOutCubic
    const t = u < 0.5 ? 4 * u * u * u : 1 - Math.pow(-2 * u + 2, 3) / 2;

    const x = quad(t, 0, cx, dx);
    const y = quad(t, 0, cy, dy);
    const r = rotation * t;

    keyframes.push({
      transform: `translate3d(${x}px, ${y}px, 0) rotate(${r}deg)`,
      offset: u,
    });
  }

  return new Promise((resolve) => {
    setTimeout(() => {
      const anim = cell.animate(keyframes, {
        duration,
        easing: 'linear', // キーフレーム側で進行を制御するため linear
        fill: 'forwards',
      });
      anim.addEventListener('finish', resolve);
    }, waveDelay);
  });
}

/**
 * シャッフルアニメーション（クロスオーバー型）
 * 各ピースが新しい位置へ滑らかに移動する
 *
 * @param {Object} _def - スキル定義（未使用）
 * @param {Object|null} _target - ターゲット（未使用）
 * @returns {Promise<void>}
 */
export async function scatterPieces(_def, _target) {
  const board = getGameBoard();
  // 1. シャッフル前に全DOM要素への参照を収集
  const cellMap = new Map();
  for (let row = 0; row < ROWS; row++) {
    for (let col = 0; col < COLS; col++) {
      const cell = document.querySelector(`[data-row="${row}"][data-col="${col}"]`);
      if (cell) {
        cellMap.set(`${row},${col}`, cell);
      }
    }
  }

  // 2. シャッフル実行して移動情報を取得
  const transitions = board.shuffleBoard();

  if (transitions.length === 0) {
    return; // 移動なし
  }

  // 3. 各ピースのDOM要素をアニメーション（波状開始 + 放物線移動 + 回転）
  const animationPromises = transitions.map((transition, index) => {
    const { from, to } = transition;
    const cell = cellMap.get(`${from.row},${from.col}`);

    if (!cell) return Promise.resolve();

    // 移動距離を計算
    const deltaRow = to.row - from.row;
    const deltaCol = to.col - from.col;
    const translateX = deltaCol * PIECE_SIZE;
    const translateY = deltaRow * PIECE_SIZE;

    // 回転角度（360度の倍数で、傾かないように）
    const rotationMultiplier = Math.floor(Math.random() * 5) - 2; // -2, -1, 0, 1, 2
    const rotation = rotationMultiplier * 360; // -720, -360, 0, 360, 720度

    cell.style.zIndex = '10';

    // 放物線アニメーション実行
    return animateArc({
      cell,
      translateX,
      translateY,
      rotation,
      index,
      duration: 800,
    });
  });

  // 4. 全アニメーション完了を待機（最後のピースの完了まで）
  await Promise.all(animationPromises);

  // 5. アニメーション完了後、DOM全体を再レンダリング（Board状態に同期）
  const syncUpdates = [];
  for (let row = 0; row < ROWS; row++) {
    for (let col = 0; col < COLS; col++) {
      const piece = board.getPiece(row, col);
      syncUpdates.push({ row, col, piece: piece || null });
    }
  }

  await syncBoardPieces(syncUpdates);

  // 6. RAF待機でDOM反映を確実に
  await new Promise((resolve) => requestAnimationFrame(resolve));
}

/**
 * 実行（シャッフル処理）
 *
 * 処理順序:
 * 1. シャッフルアニメーション（scatterPieces()で実施済み）
 * 2. カスケード実行（マッチ解決）
 *
 * 注意: 実際のシャッフル処理とDOM更新は scatterPieces() 内で完了済み
 *
 * @param {Object} _def - スキル定義（未使用）
 * @param {Object|null} _target - ターゲット（未使用）
 * @returns {Promise<{ok: boolean, reason?: string}>}
 */
export async function execute(_def, _target) {
  const board = getGameBoard();

  try {
    // シャッフル＋アニメーションは scatterPieces() で実施済み
    // Board内部の状態も既に更新済み

    // カスケード実行（初期マッチがあれば解決）
    await runCascadeFromRemovals(board, []);

    return { ok: true };
  } catch (error) {
    MinimalErrorTracker.error('SKILL', 'Shuffle execution failed', error);
    return { ok: false, reason: 'shuffle_failed', error };
  }
}
